// ABOUTME: Toast notification for achievement unlocks
// ABOUTME: Slides in from bottom with badge bounce and shimmer effect

import { TrophyIcon } from '@heroicons/react/24/outline'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { duration, easing } from '@/lib/utils/animations'

interface AchievementToastProps {
  title: string
  description?: string
  icon?: React.ReactNode
  isActive: boolean
  onComplete: () => void
  autoDismissMs?: number
}

export function AchievementToast({
  title,
  description,
  icon,
  isActive,
  onComplete,
  autoDismissMs = 3000,
}: AchievementToastProps) {
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    if (!isActive) return

    const timer = setTimeout(onComplete, autoDismissMs)
    return () => clearTimeout(timer)
  }, [isActive, onComplete, autoDismissMs])

  const handleDismiss = useCallback(() => {
    onComplete()
  }, [onComplete])

  const toastVariants = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 100, scale: 0.9 },
        animate: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            duration: duration.enter,
            ease: easing.enter,
          },
        },
        exit: {
          opacity: 0,
          y: 50,
          transition: {
            duration: duration.exit,
            ease: easing.exit,
          },
        },
      }

  const iconVariants = prefersReducedMotion
    ? undefined
    : {
        initial: { scale: 1 },
        animate: {
          scale: [1, 1.15, 1],
          transition: {
            delay: 0.1,
            duration: 0.3,
            ease: easing.bounce,
          },
        },
      }

  return (
    <AnimatePresence>
      {isActive && (
        <motion.button
          type="button"
          className="fixed bottom-24 left-4 right-4 z-50 mx-auto max-w-sm"
          variants={toastVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={handleDismiss}
          aria-label="Dismiss achievement"
        >
          <div className="relative overflow-hidden rounded-lg bg-surface-elevated border-l-4 border-editorial-gold shadow-[0_4px_24px_rgba(0,0,0,0.15)]">
            {/* Gold accent border */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-editorial-gold" />

            <div className="flex items-center gap-4 p-4 pl-5">
              {/* Icon */}
              <motion.div
                className="flex-shrink-0 w-12 h-12 rounded-full bg-editorial-gold flex items-center justify-center shadow-lg"
                variants={iconVariants}
                initial="initial"
                animate="animate"
              >
                {icon || <TrophyIcon className="w-6 h-6 text-white" />}
              </motion.div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--color-accent)] uppercase tracking-wider">
                  Achievement Unlocked!
                </p>
                <h3 className="text-base font-semibold text-[var(--color-fg-primary)] truncate uppercase tracking-wider">
                  {title}
                </h3>
                {description && (
                  <p className="text-sm text-[var(--color-fg-secondary)] truncate">{description}</p>
                )}
              </div>
            </div>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  )
}
